import React from 'react';
import { api, downloadFile } from '../../shared/api';
import { Btn, Card, Donut, StatCard, Tabs, ui } from '../../shared/ui';
import { BUY, SELL, GOLD, STATUS_COLOR, STATUS_LABEL, eur, grams, pct, qtyShort, inputStyle, type Status } from './ptShared';

type Row = {
    label: string; count: number; buy_g: number; sell_g: number; active: number; triggered: number; executed: number; cancelled: number; expired: number;
    executed_g: number; executed_eur: number; execution_rate_pct: number | null; avg_slippage_pct: number | null; avg_hours_to_trigger: number | null;
};
type Report = { group: string; from: string; to: string; rows: Row[]; total: Row | null };
type Group = 'client' | 'user' | 'month';

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function PriceTriggerReports({ token, canSeeAll, scope }: { token: string; canSeeAll: boolean; scope: 'all' | 'mine' }) {
    const [group, setGroup] = React.useState<Group>('client');
    const [from, setFrom] = React.useState(iso(new Date(Date.now() - 90 * 864e5)));
    const [to, setTo] = React.useState(iso(new Date()));
    const [data, setData] = React.useState<Report | null>(null);
    const [err, setErr] = React.useState<string | null>(null);
    const qs = `from=${from}&to=${to}&scope=${scope}`;

    React.useEffect(() => {
        let off = false;
        setErr(null);
        api<Report>(`/price-triggers/report?group=${group}&${qs}`, token).then((r) => { if (!off) setData(r); }).catch((e) => !off && setErr(String(e?.message ?? e)));
        return () => { off = true; };
    }, [group, qs, token]);

    const tot = data?.total;
    const groups: Array<{ key: Group; label: string }> = [{ key: 'client', label: 'Po klijentu' }, ...(canSeeAll ? [{ key: 'user' as Group, label: 'Po korisniku' }] : []), { key: 'month', label: 'Po mjesecu' }];
    const maxG = Math.max(1, ...(data?.rows ?? []).map((r) => r.buy_g + r.sell_g));
    const statuses: Status[] = ['active', 'triggered', 'executed', 'cancelled', 'expired'];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card pad={14}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Tabs tabs={groups} active={group} onChange={setGroup} size="sm" />
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
                        <span style={{ fontSize: '0.78rem', color: ui.muted }}>Uneseno od</span>
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, width: 150, height: 36 }} />
                        <span style={{ fontSize: '0.78rem', color: ui.muted }}>do</span>
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, width: 150, height: 36 }} />
                        <Btn size="sm" onClick={() => downloadFile(`/price-triggers/export?${qs}`, token, `triggeri-kupnje_${from}_${to}.xlsx`)}>Izvoz XLSX</Btn>
                    </div>
                </div>
            </Card>
            {err ? <div style={{ color: SELL }}>{err}</div> : null}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                <StatCard icon="Σ" color="#2f6fb0" value={tot?.count ?? 0} label="Okidača u razdoblju" foot={tot ? `kupnja ${qtyShort(tot.buy_g)} · prodaja ${qtyShort(tot.sell_g)}` : undefined} />
                <StatCard icon="✓" color={BUY} value={tot?.execution_rate_pct != null ? pct(tot.execution_rate_pct, 1) : '-'} label="Stopa izvršenja" foot="izvršeni / zatvoreni" />
                <StatCard icon="±" color={GOLD} value={tot?.avg_slippage_pct != null ? pct(tot.avg_slippage_pct, 2) : '-'} label="Prosječni odmak od cilja" foot="pozitivno = bolje od cilja" />
                <StatCard icon="⏱" color="#6b6f7d" value={tot?.avg_hours_to_trigger != null ? `${eur(tot.avg_hours_to_trigger, 1)} h` : '-'} label="Prosječno do okidanja" foot={tot ? `izvršeno ${qtyShort(tot.executed_g)}, ${eur(tot.executed_eur, 0)} EUR` : undefined} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 2fr', gap: 16 }}>
                <Card pad={18}>
                    <div style={{ fontWeight: 800, color: ui.ink, marginBottom: 14 }}>Ishod okidača</div>
                    <Donut size={150} centerLabel="okidača" centerValue={tot?.count ?? 0}
                        segments={statuses.map((s) => ({ label: STATUS_LABEL[s], value: tot ? (tot[s] as number) : 0, color: STATUS_COLOR[s] }))} />
                </Card>
                <Card pad={18}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontWeight: 800, color: ui.ink }}>Gramaža {group === 'client' ? 'po klijentu' : group === 'user' ? 'po korisniku' : 'po mjesecu'}</div>
                        <div style={{ display: 'flex', gap: 12, fontSize: '0.76rem', color: ui.ink2 }}>
                            <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: BUY, marginRight: 5 }} />kupnja</span>
                            <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: SELL, marginRight: 5 }} />prodaja</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, maxHeight: 260, overflowY: 'auto' }}>
                        {(data?.rows ?? []).slice(0, 15).map((r) => (
                            <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 90px', gap: 10, alignItems: 'center' }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: 650, color: ui.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.label}>{r.label}</div>
                                <div style={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden', background: '#eef0f6' }}>
                                    <div style={{ width: `${(r.buy_g / maxG) * 100}%`, background: BUY }} />
                                    <div style={{ width: `${(r.sell_g / maxG) * 100}%`, background: SELL }} />
                                </div>
                                <div style={{ fontSize: '0.8rem', fontWeight: 700, textAlign: 'right', color: ui.ink }}>{qtyShort(r.buy_g + r.sell_g)}</div>
                            </div>
                        ))}
                        {data && data.rows.length === 0 ? <div style={{ color: ui.muted, fontSize: '0.86rem' }}>Nema okidača u razdoblju.</div> : null}
                    </div>
                </Card>
            </div>

            <Card>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd', color: ui.ink2, textAlign: 'right' }}>
                                {[group === 'client' ? 'Klijent' : group === 'user' ? 'Korisnik' : 'Mjesec', 'Broj', 'Kupnja g', 'Prodaja g', 'Aktivni', 'Okinuti', 'Izvršeni', 'Odbačeni', 'Istekli', 'Izvršeno g', 'Izvršeno EUR', 'Stopa', 'Odmak'].map((h, i) => (
                                    <th key={h} style={{ padding: '11px 12px', fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: i === 0 ? 'left' : 'right', borderBottom: `1px solid ${ui.line}`, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[...(data?.rows ?? []), ...(tot ? [{ ...tot, label: 'Ukupno' }] : [])].map((r, i, arr) => {
                                const isTot = tot && i === arr.length - 1;
                                return (
                                    <tr key={r.label + i} style={{ borderBottom: `1px solid ${ui.line2}`, fontWeight: isTot ? 800 : 500, background: isTot ? '#fafbfd' : undefined }}>
                                        <td style={{ padding: '9px 12px', color: ui.ink }}>{r.label}</td>
                                        {[r.count, grams(r.buy_g), grams(r.sell_g), r.active, r.triggered, r.executed, r.cancelled, r.expired, grams(r.executed_g), eur(r.executed_eur), r.execution_rate_pct != null ? pct(r.execution_rate_pct, 1) : '-', r.avg_slippage_pct != null ? pct(r.avg_slippage_pct, 2) : '-'].map((v, k) => (
                                            <td key={k} style={{ padding: '9px 12px', textAlign: 'right', color: ui.ink2, whiteSpace: 'nowrap' }}>{v}</td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
